/**
 * SwipeCard — the hero gesture primitive (design system, FOUNDATION — v5
 * "PIXEL SWEETHEART"; PRD §2 / S-10, DESIGN-BRIEF v5 §5 "bouncy, cute,
 * delightful").
 *
 * Physical, weighty, warm:
 *  - drag → translate + rotate follows the finger with spring physics; the
 *    photo PARALLAXES inside the frame (content counter-shifts, edges hidden
 *    by a 1.06 scale); a candy wash + pixel sticker (LIKE mint / REJECT
 *    bubblegum — EQUAL visual weight, ©deck.swipe_labels) scales in with
 *    drag distance; a soft candy trail breathes off the trailing edge;
 *  - release past the threshold (or a flick) → the verdict COMMITS
 *    IMMEDIATELY: `onVerdict(verdict, handoff)` fires with the live drag
 *    offset + velocity so the consumer can seed a seamless fly-off ghost
 *    (the deck's flight layer). The card itself resets silently — keyed
 *    consumers remount it for the next card; keyless consumers (gallery)
 *    get a fresh rise-from-stack via the generation key;
 *  - drag UP past its own threshold → `onSwipeUp` (the deck opens the full
 *    profile); a chevroned PROFILE tag hints while pulling up;
 *  - below every threshold the card springs back with heavy mass;
 *  - on fine-pointer devices the resting card tilts subtly toward the cursor;
 *  - a real drag suppresses the click that would otherwise fire on release,
 *    so photo tap-zones inside the card never misfire after a swipe.
 *
 * The deck vertical supplies content + the button action bar (every swipe
 * action must have a button equivalent — PRD §10) and MUST treat the verdict
 * as a server proposal: the server re-validates every swipe at write time
 * (ARCH §2.3).
 *
 * Programmatic swipes (action-bar buttons, keyboard): grab a ref and call
 * `ref.current?.swipe("like" | "reject" | "super_reject")` — same immediate
 * commit, same callback. API backward-compatible with v1-v4 (the handoff
 * param and onSwipeUp are additive).
 */
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import {
  animate,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type PanInfo,
} from "framer-motion";
import { copy } from "@humble/shared";
import { prefersReducedMotion, springs } from "./motion.js";

export type SwipeVerdict = "like" | "reject" | "super_reject";

/** Live gesture state at commit time — lets the consumer's fly-off ghost
 *  start exactly where the finger let go (buttons/keyboard pass zeros). */
export interface SwipeHandoff {
  x: number;
  y: number;
  velocityX: number;
}

export interface SwipeCardHandle {
  /** Commit a verdict programmatically — same immediate onVerdict path. */
  swipe(verdict: SwipeVerdict): void;
}

export interface SwipeCardProps {
  onVerdict?: (verdict: SwipeVerdict, handoff?: SwipeHandoff) => void;
  /** Swipe-up intent (the deck opens the full profile). Additive, optional. */
  onSwipeUp?: () => void;
  disabled?: boolean;
  className?: string;
  /** Rise-from-the-stack entrance (the "next card pops from the deck" feel). Default on. */
  popIn?: boolean;
  children?: ReactNode;
}

const THRESHOLD_PX = 110;
const FLICK_VELOCITY = 700;
const UP_THRESHOLD_PX = 90;
const UP_FLICK_VELOCITY = 800;
const TILT_DEG = 5;

function fine(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
    !prefersReducedMotion()
  );
}

export const SwipeCard = forwardRef<SwipeCardHandle, SwipeCardProps>(function SwipeCard(
  { onVerdict, onSwipeUp, disabled = false, className, popIn = true, children },
  ref,
) {
  const dragging = useRef(false);
  const committed = useRef(false);
  const suppressClickUntil = useRef(0);
  const [generation, setGeneration] = useState(0);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-320, 320], [-16, 16]);

  // photo parallax — the content layer counter-shifts inside the frame
  const parallaxX = useTransform(x, [-320, 320], [24, -24]);
  const parallaxY = useTransform(y, [-140, 64], [10, -7]);

  // directional candy trail — a soft color breath off the trailing edge
  // (mint for like, bubblegum for reject — matches the v5 verb tokens)
  const trailShadow = useTransform(x, (v) => {
    const t = Math.min(Math.abs(v) / 170, 1);
    if (t < 0.04) return "var(--shadow-2)";
    const rgb = v > 0 ? "87, 219, 162" : "249, 132, 197";
    const off = (v > 0 ? -1 : 1) * Math.round(18 * t);
    return `var(--shadow-2), ${off}px 0 ${Math.round(24 * t)}px rgba(${rgb}, ${(0.26 * t).toFixed(2)}), ${off * 2}px 0 ${Math.round(52 * t)}px rgba(${rgb}, ${(0.12 * t).toFixed(2)})`;
  });

  // cursor tilt (desktop, resting card only) — spring-smoothed
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(tiltX, { stiffness: 260, damping: 22 });
  const rotateY = useSpring(tiltY, { stiffness: 260, damping: 22 });

  // washes + stickers scale in with drag distance (clamped)
  const likeOpacity = useTransform(x, [16, THRESHOLD_PX], [0, 1]);
  const likeScale = useTransform(x, [16, THRESHOLD_PX], [0.7, 1.06]);
  const rejectOpacity = useTransform(x, [-THRESHOLD_PX, -16], [1, 0]);
  const rejectScale = useTransform(x, [-THRESHOLD_PX, -16], [1.06, 0.7]);
  const upOpacity = useTransform(y, [-UP_THRESHOLD_PX, -28], [1, 0]);
  const upScale = useTransform(y, [-UP_THRESHOLD_PX, -28], [1.04, 0.72]);

  function commit(verdict: SwipeVerdict, handoff: SwipeHandoff) {
    if (disabled || committed.current) return;
    committed.current = true;
    // reset silently for reuse — keyed consumers (the deck) remount by key;
    // keyless consumers (gallery) get a fresh rise-from-stack entrance
    x.set(0);
    y.set(0);
    tiltX.set(0);
    tiltY.set(0);
    setGeneration((g) => g + 1);
    onVerdict?.(verdict, handoff);
    // re-arm on the next macrotask — the parent has advanced (or chosen not to)
    window.setTimeout(() => {
      committed.current = false;
    }, 0);
  }

  useImperativeHandle(ref, () => ({
    swipe(verdict: SwipeVerdict) {
      commit(verdict, { x: 0, y: 0, velocityX: 0 });
    },
  }));

  function onDragStart() {
    dragging.current = true;
    tiltX.set(0);
    tiltY.set(0);
  }

  function springBack() {
    void animate(x, 0, springs.weighty);
    void animate(y, 0, springs.weighty);
  }

  function onDragEnd(_: unknown, info: PanInfo) {
    dragging.current = false;
    // a real drag must not leak a click into the tap-zones underneath
    if (Math.abs(info.offset.x) > 6 || Math.abs(info.offset.y) > 6) {
      suppressClickUntil.current = Date.now() + 220;
    }
    if (disabled || committed.current) return;
    const offX = x.get();
    const offY = y.get();
    const vx = info.velocity.x;
    const vy = info.velocity.y;

    // swipe-up → open profile (vertical intent must dominate)
    if (
      onSwipeUp &&
      Math.abs(offY) > Math.abs(offX) &&
      (offY <= -UP_THRESHOLD_PX || vy <= -UP_FLICK_VELOCITY)
    ) {
      onSwipeUp();
      springBack();
      return;
    }

    if (Math.abs(offX) >= THRESHOLD_PX || Math.abs(vx) >= FLICK_VELOCITY) {
      const verdict: SwipeVerdict =
        (Math.abs(offX) >= THRESHOLD_PX ? offX > 0 : vx > 0) ? "like" : "reject";
      commit(verdict, { x: offX, y: offY, velocityX: vx });
    } else {
      // heavy-mass return — the card WEIGHS something
      springBack();
    }
  }

  function onClickCapture(e: MouseEvent<HTMLDivElement>) {
    if (Date.now() < suppressClickUntil.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (disabled || dragging.current || !fine()) return;
    const r = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 2 * TILT_DEG);
    tiltX.set(-((e.clientY - r.top) / r.height - 0.5) * 2 * TILT_DEG);
  }

  function onPointerLeave() {
    tiltX.set(0);
    tiltY.set(0);
  }

  const labels = copy.deck.swipe_labels; // { left, right, super, crush, up }

  return (
    <motion.div
      key={generation}
      className={`swipe-card${className ? ` ${className}` : ""}`}
      style={{
        x,
        y,
        rotate,
        rotateX,
        rotateY,
        boxShadow: trailShadow,
        transformPerspective: 900,
      }}
      drag={!disabled}
      dragElastic={0.9}
      dragConstraints={{ top: -140, bottom: 56 }}
      dragSnapToOrigin={false}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClickCapture={onClickCapture}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      whileDrag={{ cursor: "grabbing" }}
      initial={popIn ? { scale: 0.84, y: 46, opacity: 0 } : false}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      transition={springs.bouncy}
      aria-disabled={disabled}
    >
      {/* parallax layer — the photo drifts against the drag, depth for free */}
      <motion.div
        className="swipe-card__parallax"
        style={{ x: parallaxX, y: parallaxY }}
      >
        {children}
      </motion.div>
      {/* equal-weight washes — identical label treatment, only color + side differ */}
      <motion.div
        className="swipe-card__wash swipe-card__wash--like"
        style={{ opacity: likeOpacity }}
        aria-hidden="true"
      >
        <motion.span className="swipe-card__label" style={{ scale: likeScale }}>
          {labels.right}
        </motion.span>
      </motion.div>
      <motion.div
        className="swipe-card__wash swipe-card__wash--reject"
        style={{ opacity: rejectOpacity }}
        aria-hidden="true"
      >
        <motion.span className="swipe-card__label" style={{ scale: rejectScale }}>
          {labels.left}
        </motion.span>
      </motion.div>
      {onSwipeUp && (
        <motion.div
          className="swipe-card__wash swipe-card__wash--up"
          style={{ opacity: upOpacity }}
          aria-hidden="true"
        >
          <motion.span className="swipe-card__label" style={{ scale: upScale }}>
            {labels.up}
          </motion.span>
        </motion.div>
      )}
    </motion.div>
  );
});
